import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import {
  SETTINGS_GROUPS,
  SettingsSubNav,
  type SubNavItem,
} from '@/components/layout/SettingsSubNav';

export const Route = createFileRoute('/settings/')({
  component: SettingsIndexPage,
});

/**
 * Settings-Index. UX-Carry-Over Sprint 4:
 *
 * Vorher: 7+ Cards in einer Wand (kein klares Gruppen-Modell, mobile-unfreundlich).
 * Jetzt: 2-Spalten-Layout - links Sticky-SubNav (datendriven aus
 *   `SETTINGS_GROUPS`), rechts Content-Pane mit grouped Cards.
 *
 * Wichtig: bestehende Routes wie /settings/import bleiben unverändert
 * (kein Breaking Change). Die SubNav verlinkt nur dorthin.
 */

function SettingsIndexPage(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Einstellungen</h1>
        <p className="text-muted">
          App-Konfiguration, Datenpflege, Integrationen, Sicherheit, Wartungsroutinen.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
        <aside className="md:sticky md:top-4 md:max-h-[calc(100vh-3rem)] md:overflow-y-auto md:pr-2">
          <SettingsSubNav />
        </aside>

        <div className="flex flex-col gap-4">
          {SETTINGS_GROUPS.map((group) => (
            <section
              key={group.id}
              aria-labelledby={`section-${group.id}`}
              data-testid={`section-${group.id}`}
            >
              <h2
                id={`section-${group.id}`}
                className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted"
              >
                {group.label}
              </h2>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {group.items.map((item, idx) => (
                  <SettingsCard key={`${group.id}-${idx}`} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function SettingsCard({ item }: { item: SubNavItem }): JSX.Element {
  const body = (
    <Card>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-primary">{item.icon}</span>
        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <span>{item.label}</span>
            {item.sprintStub && <Badge tone="primary">{item.sprintStub}</Badge>}
          </h3>
          {item.description && (
            <p className="mt-1 text-xs text-muted">{item.description}</p>
          )}
        </div>
        {!item.disabled && (
          <ArrowRight className="size-4 text-muted" aria-hidden="true" />
        )}
      </div>
    </Card>
  );

  if (item.disabled) {
    return (
      <div
        className="cursor-not-allowed opacity-60"
        title={item.sprintStub ?? 'Bald verfügbar'}
        aria-disabled="true"
      >
        {body}
      </div>
    );
  }
  return (
    <Link to={item.to} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      {body}
    </Link>
  );
}
